import React from 'react';
import { motion } from 'framer-motion';

const WorkExperienceRetroDual = () => {
  const experiences = [
    {
      id: 6,
      role: "Product Engineer I",
      company: "MISUMI",
      period: "August 2025 - Current",
      type: "Full-time",
      description: [
        "Processed custom quoting opportunities between customers and manufacturers, coordinating production requirements and specifications to support manufacturing operations.",
        "Reviewed customer 2D drawings for completeness and resolved discrepancies through systematic analysis, ensuring manufacturability and design compliance.",
        "Served as primary liaison between customers and manufacturing teams, communicating technical requirements and specifications to optimize production processes."
      ],
      skills: ["2D Drawing Review", "CAD Analysis", "DFM", "Technical Documentation"],
      current: true
    },
    {
      id: 5,
      role: "CAD Research Assistant",
      company: "University of Wisconsin-Madison, Department of Mechanical Engineering",
      period: "January 2025 - May 2025",
      type: "Part-time",
      description: [
        "Designed and assembled four preliminary CAD models to support the professor's funding proposals for future research projects.",
        "Developed a custom rolling table to securely transport a $500,000 laser system between departments, ensuring mobility and structural stability.",
        "Collaborating with faculty and researchers to refine the design for manufacturability and usability."
      ],
      skills: ["CAD Design", "Research", "Structural Analysis", "DFM"]
    },
    {
      id: 4,
      role: "Mechanical Engineering Intern",
      company: "Acciona Energia",
      period: "May 2024 - August 2024",
      type: "Internship",
      description: [
        "Led daily status meetings to review, update, and track wind turbine components from ExWorks to Delivery, ensuring project and contract milestones were met.",
        "Monitored and resolved mechanical Non-Conformance Reports, coordinating timely actions and closeout.",
        "Compiled \"Lessons Learned\" reports by analyzing issue trends across projects, and proposed steps for process improvement."
      ],
      skills: ["Project Management", "Quality Control", "Wind Energy", "Process Improvement"]
    },
    {
      id: 3,
      role: "Associate Mechanical Engineer, Co-op",
      company: "Extreme Engineering Solutions",
      period: "May 2023 - December 2023",
      type: "Co-op",
      description: [
        "Designed 3D models of metalwork for embedded computing systems, adhering to project specifications and ensuring manufacturability across a variety of projects and platforms.",
        "Worked closely with the manufacturing team to revise and update existing designs, ensuring continuity and compatibility across platforms.",
        "Created and maintained detailed technical drawings and Bills of Materials, ensuring accuracy and completeness in support of manufacturing and assembly processes.",
        "Conducted detailed analysis of low-cycle thermal fatigue for BGA packages, pinpointing failure modes and vulnerabilities."
      ],
      skills: ["CAD Design", "Manufacturing", "Technical Drawing", "Thermal Analysis"]
    },
    {
      id: 2,
      role: "Lab Technician",
      company: "University of Wisconsin-Madison, Design Innovation Lab",
      period: "January 2022 - January 2023",
      type: "Part-time",
      description: [
        "Educated hundreds of students on the basics of design and manufacturing to ensure proper and productive operation of machining equipment.",
        "Spearheaded the creation of multiple projects aimed at enhancing the practical functionality of the laboratory, contributing to an improved daily workflow.",
        "Maintained working order of complex tooling and equipment ensuring the safety and well-being of the University's student body."
      ],
      skills: ["Teaching", "Equipment Maintenance", "Safety", "Manufacturing"]
    },
    {
      id: 1,
      role: "Logistics Intern",
      company: "Tara International",
      period: "May 2022 - August 2022",
      type: "Internship",
      description: [
        "Increased on-time shipping by 10% by coordinating daily traffic of regionally transported goods.",
        "Improved quality control of product with the use of an organized procedure when loading and unloading material.",
        "Communicated with third-party freight lines to build integrity and respect during delivery delays."
      ],
      skills: ["Logistics", "Quality Control", "Communication", "Process Optimization"]
    }
  ];

  const getTypeColors = (type) => {
    switch (type) {
      case 'Co-op': 
        return { color: 'bg-purple-400', gradient: 'from-purple-400 to-pink-500', darkBorder: 'dark:border-purple-400' };
      case 'Internship': 
        return { color: 'bg-emerald-400 dark:bg-pink-400', gradient: 'from-emerald-400 to-teal-500 dark:from-pink-400 dark:to-purple-500', darkBorder: 'dark:border-pink-400' };
      case 'Part-time': 
        return { color: 'bg-green-400', gradient: 'from-green-400 to-emerald-500', darkBorder: 'dark:border-green-400' };
      case 'Full-time': 
        return { color: 'bg-cyan-400', gradient: 'from-cyan-400 to-blue-500', darkBorder: 'dark:border-cyan-400' };
      default: 
        return { color: 'bg-gray-400', gradient: 'from-gray-400 to-gray-600', darkBorder: 'dark:border-gray-400' };
    }
  };

  return (
    <div className="min-h-screen bg-[#F5E6D3] dark:bg-[#1a1a2e] relative overflow-hidden transition-colors duration-500">
      {/* Dot Grid Background - Light */}
      <div 
        className="absolute inset-0 opacity-20 dark:opacity-0 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #8B7355 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />
      
      {/* Dot Grid Background - Dark (Glowing) */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-30 transition-opacity duration-500"
        style={{
          backgroundImage: `radial-gradient(circle, #00ff88 1px, transparent 1px)`,
          backgroundSize: '24px 24px'
        }}
      />

      {/* Scanline Effect - Dark Mode */}
      <div 
        className="absolute inset-0 opacity-0 dark:opacity-5 pointer-events-none transition-opacity duration-500"
        style={{
          backgroundImage: `repeating-linear-gradient(0deg, transparent, transparent 2px, #00ff88 2px, #00ff88 4px)`
        }}
      />

      <div className="relative max-w-6xl mx-auto px-4 py-12">
        
        {/* Page Header */}
        <motion.div
          className="text-center mb-8"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-block bg-white dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 px-8 py-4 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,255,0.5)] mb-4 transition-all duration-500">
            <h1 className="text-5xl font-black uppercase tracking-tight transition-colors duration-500">
              WORK <span className="text-orange-600 dark:text-pink-400 dark:drop-shadow-[0_0_20px_rgba(255,0,255,0.8)]">EXPERIENCE</span>
            </h1>
          </div>
          <p className="text-xl font-bold text-gray-800 dark:text-green-400 max-w-2xl mx-auto transition-colors duration-500">
            💼 Professional Journey Through Engineering Roles
          </p>
        </motion.div>

        {/* Resume Download Button */}
        <motion.div 
          className="flex justify-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          <a
            href="/assets/other/Connor_Madigan_Resume.pdf"
            download="Connor_Madigan_Resume.pdf"
            className="bg-yellow-400 hover:bg-yellow-500 border-4 border-black dark:border-yellow-400 px-8 py-4 font-black text-xl uppercase shadow-[6px_6px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(255,255,0,0.6)] hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[0_0_30px_rgba(255,255,0,0.8)] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] inline-flex items-center gap-3 text-black duration-500"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            Download Resume
          </a>
        </motion.div>

        {/* Experience Windows */}
        <div className="space-y-6">
          {experiences.map((experience, index) => {
            const colors = getTypeColors(experience.type);
            return (
              <motion.div
                key={experience.id}
                className={`bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black ${colors.darkBorder} shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[8px_8px_0px_0px_currentColor] hover:shadow-[10px_10px_0px_0px_rgba(0,0,0,1)] dark:hover:shadow-[10px_10px_0px_0px_currentColor] transition-all hover:translate-x-[-2px] hover:translate-y-[-2px] duration-500`}
                initial={{ opacity: 0, x: -30 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                {/* Window Title Bar */}
                <div className={`bg-gradient-to-r ${colors.gradient} border-b-4 border-black ${colors.darkBorder} px-4 py-3 flex items-center justify-between transition-all duration-500`}>
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-red-500 border-2 border-black dark:shadow-[0_0_10px_rgba(255,0,0,0.8)]"></div>
                      <div className="w-4 h-4 rounded-full bg-yellow-300 border-2 border-black dark:shadow-[0_0_10px_rgba(255,255,0,0.8)]"></div>
                      <div className="w-4 h-4 rounded-full bg-green-400 border-2 border-black dark:shadow-[0_0_10px_rgba(0,255,0,0.8)]"></div>
                    </div>
                    <span className="font-black text-sm uppercase tracking-tight ml-2 text-black dark:text-white transition-colors duration-500">
                      JOB_{String(experience.id).padStart(2, '0')}.exe
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {experience.current && (
                      <div className="bg-green-400 border-2 border-black dark:border-green-400 px-3 py-1 animate-pulse dark:shadow-[0_0_15px_rgba(0,255,0,0.8)] transition-all duration-500">
                        <span className="font-black text-xs uppercase text-black">● CURRENT</span>
                      </div>
                    )}
                    <div className={`${colors.color} border-2 border-black ${colors.darkBorder} px-3 py-1 transition-all duration-500`}>
                      <span className="font-black text-xs uppercase text-black">{experience.type}</span>
                    </div>
                  </div>
                </div>

                {/* Window Content */}
                <div className="p-6">
                  {/* Job Header */}
                  <div className="mb-4">
                    <h2 className="text-3xl font-black uppercase mb-2 leading-tight dark:text-white transition-colors duration-500">
                      {experience.role}
                    </h2>
                    <h3 className="text-xl font-bold text-blue-600 dark:text-cyan-400 mb-2 transition-colors duration-500">
                      {experience.company}
                    </h3>
                    <div className="inline-block bg-black text-white dark:bg-cyan-400 dark:text-black px-4 py-1 border-2 border-black dark:border-cyan-400 dark:shadow-[0_0_15px_rgba(0,255,255,0.5)] transition-all duration-500">
                      <span className="font-bold text-sm">📅 {experience.period}</span>
                    </div>
                  </div>

                  {/* Description */}
                  <div className="bg-white dark:bg-[#1a1a2e] border-3 border-black dark:border-cyan-400 p-4 mb-4 transition-all duration-500">
                    <div className="space-y-2">
                      {experience.description.map((bullet, bulletIndex) => (
                        <div
                          key={bulletIndex}
                          className="flex items-start gap-2"
                        >
                          <div className="w-3 h-3 bg-black dark:bg-cyan-400 mt-1.5 flex-shrink-0"></div>
                          <p className="text-sm font-bold text-gray-800 dark:text-gray-300 leading-relaxed transition-colors duration-500">
                            {bullet}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Skills */}
                  <div className="flex flex-wrap gap-2">
                    {experience.skills.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="px-3 py-1 bg-gray-200 dark:bg-[#2d2d44] border-2 border-black dark:border-cyan-400 text-xs font-black uppercase hover:bg-gray-300 dark:hover:bg-cyan-400 dark:hover:text-black transition-colors text-black dark:text-cyan-300 duration-500"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Stats Window */}
        <motion.div
          className="mt-12 bg-[#FFF8E7] dark:bg-[#2d2d44] border-4 border-black dark:border-cyan-400 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_20px_rgba(0,255,255,0.5)] transition-all duration-500"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          {/* Window Title Bar */}
          <div className="bg-gradient-to-r from-orange-400 to-red-500 dark:from-cyan-400 dark:to-blue-500 border-b-4 border-black dark:border-cyan-400 px-4 py-3 flex items-center justify-between transition-all duration-500">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-black dark:bg-cyan-400 dark:shadow-[0_0_10px_rgba(0,255,255,0.8)]"></div>
              <span className="font-black text-lg uppercase tracking-tight text-black dark:text-cyan-300 transition-colors duration-500">📊 STATS.exe</span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-cyan-400 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,255,0.5)] transition-all duration-500">
                <div className="text-5xl font-black text-blue-600 dark:text-cyan-400 mb-2 dark:drop-shadow-[0_0_15px_rgba(0,255,255,0.8)] transition-all duration-500">
                  {experiences.length}
                </div>
                <div className="font-black uppercase text-xs tracking-tight dark:text-white transition-colors duration-500">
                  Positions Held
                </div>
              </div>
              <div className="text-center bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-green-400 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(0,255,0,0.5)] transition-all duration-500">
                <div className="text-5xl font-black text-emerald-600 dark:text-green-400 mb-2 dark:drop-shadow-[0_0_15px_rgba(0,255,0,0.8)] transition-all duration-500">
                  4+
                </div>
                <div className="font-black uppercase text-xs tracking-tight dark:text-white transition-colors duration-500">
                  Years Experience
                </div>
              </div>
              <div className="text-center bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-purple-400 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(138,43,226,0.5)] transition-all duration-500">
                <div className="text-5xl font-black text-purple-600 dark:text-purple-400 mb-2 dark:drop-shadow-[0_0_15px_rgba(138,43,226,0.8)] transition-all duration-500">
                  5
                </div>
                <div className="font-black uppercase text-xs tracking-tight dark:text-white transition-colors duration-500">
                  Companies
                </div>
              </div>
              <div className="text-center bg-white dark:bg-[#1a1a2e] border-4 border-black dark:border-pink-400 p-6 shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[0_0_15px_rgba(255,0,255,0.5)] transition-all duration-500">
                <div className="text-5xl font-black text-orange-600 dark:text-pink-400 mb-2 dark:drop-shadow-[0_0_15px_rgba(255,0,255,0.8)] transition-all duration-500">
                  3
                </div>
                <div className="font-black uppercase text-xs tracking-tight dark:text-white transition-colors duration-500">
                  Industries
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-32 right-10 w-20 h-20 bg-blue-400 dark:bg-cyan-400 border-4 border-blue-600 dark:border-cyan-400 rotate-12 hidden lg:block dark:shadow-[0_0_30px_rgba(0,255,255,0.6)] dark:animate-pulse transition-all duration-500"></div>
      <div className="absolute bottom-32 left-10 w-16 h-16 bg-yellow-400 border-4 border-yellow-600 dark:border-yellow-400 -rotate-12 hidden lg:block dark:shadow-[0_0_30px_rgba(255,255,0,0.6)] dark:animate-pulse transition-all duration-500"></div>
    </div>
  );
};

export default WorkExperienceRetroDual;